// ─────────────────────────────────────────────────────────────────────────────
// AIInsights — deterministic synthesis with localStorage caching
//
// Synthesizes all detector outputs from Behavior, Risk, Psychology, and
// Context tabs into a coaching narrative using pure JavaScript logic.
//
// Generates instantly, works offline, costs nothing, never fails.
//
// Caching: last generated result is stored in localStorage keyed by
// userId + accountId. Tab open → shows cached result immediately with a
// "Generated X days ago" timestamp + Regenerate button. No blank states.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import { useDetectors } from "../../hooks/useDetectors";

// ─── Cache helpers ─────────────────────────────────────────────────────────────
function cacheKey(userId, accountId) {
  return `fos_ai_insights_v2_${userId || "anon"}_${accountId || "all"}`;
}

function loadCache(userId, accountId) {
  try {
    const raw = localStorage.getItem(cacheKey(userId, accountId));
    if (!raw) return null;
    return JSON.parse(raw); // { insights, generatedAt }
  } catch {
    return null;
  }
}

function saveCache(userId, accountId, insights) {
  try {
    localStorage.setItem(
      cacheKey(userId, accountId),
      JSON.stringify({ insights, generatedAt: new Date().toISOString() })
    );
  } catch {
    // localStorage full or blocked — fail silently
  }
}

function formatAge(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 2)   return "just now";
  if (mins < 60)  return `${mins} minutes ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// ─── Known pattern combinations that share a root cause ───────────────────────
const PATTERN_COMBINATIONS = [
  {
    patterns: ["hour_cliff", "fear_spike"],
    rootCause: "fatigue-driven trading",
    connection: "Your worst time slot and high-fear sessions likely overlap — trading while mentally tired amplifies fear and kills your edge.",
  },
  {
    patterns: ["hour_cliff", "low_focus"],
    rootCause: "focus degradation over the session",
    connection: "Low focus sessions and your worst hour slot are probably the same trades — concentration degrades as the session wears on.",
  },
  {
    patterns: ["revenge", "impulsiveness"],
    rootCause: "emotional reactivity after losses",
    connection: "Revenge trading and high impulsiveness are the same behavioral loop — a loss triggers an emotional spike that drives the next entry.",
  },
  {
    patterns: ["revenge", "fear_spike"],
    rootCause: "loss aversion overriding your plan",
    connection: "Fear spikes and revenge trades often follow the same losing trade — fear of being wrong drives the next impulsive entry.",
  },
  {
    patterns: ["stop_widening", "manual_exit"],
    rootCause: "not trusting your own plan",
    connection: "Widening stops and manual exits are two sides of the same problem — you override your rules both at stop-loss and at target.",
  },
  {
    patterns: ["stop_widening", "stop_adherence"],
    rootCause: "stop loss discipline breakdown",
    connection: "These two patterns confirm the same leak: you set stops but consistently don't honor them.",
  },
  {
    patterns: ["size_tilt", "win_streak_euphoria"],
    rootCause: "emotional position sizing",
    connection: "You size up after both wins and losses — emotion, not analysis, is driving your position size decisions.",
  },
  {
    patterns: ["size_tilt", "revenge"],
    rootCause: "loss-driven risk escalation",
    connection: "Sizing up after losses combined with revenge trading is a classic tilt spiral — each loss increases both urgency and size.",
  },
  {
    patterns: ["low_focus", "impulsiveness"],
    rootCause: "mental state degradation",
    connection: "Low focus and high impulsiveness co-occur — when concentration slips, impulsive decisions fill the gap.",
  },
  {
    patterns: ["hour_cliff", "day_of_week"],
    rootCause: "time-dependent edge degradation",
    connection: "Your worst time slot and worst day-of-week patterns suggest your edge is highly time-dependent — certain windows consistently hurt you.",
  },
  {
    patterns: ["overtrading", "size_tilt"],
    rootCause: "escalating aggression when behind",
    connection: "Trading more AND sizing bigger when losing is classic revenge behavior — trying to win it back faster rather than trading better.",
  },
  {
    patterns: ["setup_edge", "conviction_mismatch"],
    rootCause: "setup selection discipline",
    connection: "Your worst setups and your low-conviction trades may be the same entries — you take setups you don't fully believe in.",
  },
  {
    patterns: ["mental_state", "fear_spike"],
    rootCause: "emotional state management",
    connection: "Your mental state and fear levels both affect your edge — the same underlying emotional pattern shows up across both.",
  },
  {
    patterns: ["news_event", "hour_cliff"],
    rootCause: "high-volatility session management",
    connection: "News events and your worst hour slot may overlap — volatile conditions in specific windows are compounding your edge degradation.",
  },
];

// ─── Specific rules per detector ──────────────────────────────────────────────
function buildRule(d) {
  const rules = {
    revenge:             "After any losing trade, set a 5-minute timer before placing the next entry. No exceptions.",
    size_tilt:           "Lock position size to a fixed contract count for 30 days regardless of recent losses.",
    win_streak_euphoria: "Cap position size at your baseline regardless of recent wins. Log your size decision before every entry.",
    hour_cliff:          `No new trades in your worst slot (${d.details?.worst_slot?.slot || "identified time window"}) for 30 days. Close the platform during that window.`,
    day_of_week:         `Reduce position size by 50% on ${d.details?.worst?.day || "your worst day"} for 30 days.`,
    time_in_trade:       "Set a hard exit reminder at your average win duration. When it fires, evaluate the trade.",
    overtrading:         `Cap daily trades at ${Math.ceil((d.details?.median_count || 6) * 0.75)}. When you hit the cap, close the platform for the day.`,
    size_adherence:      "Add a pre-entry checklist item: 'Does this size comply with my rules?' Do not enter if the answer is no.",
    stop_adherence:      "For 30 days, treat your stop as automated — no manual intervention. Log every time you're tempted to move it.",
    stop_widening:       "No stop adjustments after entry. Place it, don't touch it. Log every violation.",
    r_multiples:         "Only take entries where target ÷ risk ≥ 1.5. Write down target and stop before every entry.",
    drawdown_proximity:  "When daily drawdown exceeds 60% of your limit, stop trading for the day.",
    rule_violations:     "Review your violation log daily. Identify the most common violation and add a specific pre-entry check for it.",
    news_event:          "Cut position size in half on any trade you flag as a news event for 30 days.",
    manual_exit:         "For 30 days, let every trade hit its stop or target — no manual exits. Log each outcome.",
    market_regime:       `Reduce size by 50% when trading in ${d.details?.worst?.condition || "your worst market condition"}.`,
    setup_edge:          `Remove your bottom ${d.details?.leaking_setups?.length || 1} setup${(d.details?.leaking_setups?.length || 1) > 1 ? "s" : ""} from your playbook for 30 days.`,
    fear_spike:          "When fear level is ≥7, reduce size by 50% or skip the trade entirely.",
    impulsiveness:       "Before every entry, pause 90 seconds. If you still want the trade after the pause, take it.",
    low_focus:           "Only trade when focus is ≥7. Log focus level on every entry.",
    mental_state:        `On ${d.details?.worst?.state || "your worst mental state"} days, trade half size or sit out.`,
    conviction_mismatch: "Only take trades where conviction ≥7. Skip anything below that threshold for 30 days.",
  };
  return rules[d.id] || `Track ${d.title} daily for 30 days. Review every Friday.`;
}

// ─── Weekly focus — positive framing of top priority ──────────────────────────
function buildWeeklyFocus(d) {
  const focuses = {
    revenge:             "Build your pause habit — one deliberate wait after every loss, turning reactive trading into intentional trading.",
    size_tilt:           "Trade your fixed size with confidence — consistent sizing is one of the most powerful edges in futures.",
    win_streak_euphoria: "Maintain discipline through winning streaks — consistent sizing across all conditions is the hallmark of a professional.",
    hour_cliff:          "Own your best hours — concentrate all your energy on the time windows where your win rate is strong.",
    day_of_week:         "Make your best days count — focus your sharpest trading on the sessions where your edge is proven.",
    time_in_trade:       "Let your winners breathe — the data shows your winning trades benefit from patience.",
    overtrading:         "Trade less, earn more — quality over quantity is your highest-leverage improvement right now.",
    stop_adherence:      "Honor your plan — every stop you respect is a deposit in your discipline account.",
    stop_widening:       "Trust your analysis — your entry stop was placed with a clear head. Defend it.",
    news_event:          "Trade your edge, not the news — your cleanest setups come outside of news-driven volatility.",
    manual_exit:         "Let your plan work — your setups were built to reach their targets. Give them the chance.",
    market_regime:       "Trade your best conditions — knowing which regimes favor you is a genuine competitive advantage.",
    setup_edge:          "Play your A-game setups only — your data shows which patterns have real edge. Trust it.",
    fear_spike:          "Trade with clarity — on high-fear sessions, smaller size means you can stay objective.",
    impulsiveness:       "Trade deliberately — the 90-second pause turns reactive impulses into considered decisions.",
    low_focus:           "Trade only when sharp — the best trade you don't take is better than a bad trade you do.",
    mental_state:        "Know your best states — trading in your peak mental conditions is an edge most traders ignore.",
    conviction_mismatch: "Back your best ideas — only trading high-conviction setups sharpens your pattern recognition over time.",
    r_multiples:         "Focus on quality entries with favorable risk/reward — your expectancy improves most when you raise your R-multiple floor.",
  };
  return focuses[d.id] || `This week: focus on reducing the impact of ${d.title} through consistent rule application.`;
}

// ─── Main synthesis function ───────────────────────────────────────────────────
function synthesize({ allDetectors, tradeCount, winRate }) {
  const firing = allDetectors.filter(d =>
    d.confidence !== "insufficient" && d.estimated_cost > 0
  );
  const informational = allDetectors.filter(d =>
    d.confidence !== "insufficient" && d.estimated_cost === 0
  );
  const insufficient = allDetectors.filter(d => d.confidence === "insufficient");

  const wr = Math.round(winRate * 100);

  if (firing.length === 0 && informational.length === 0) {
    return {
      state_of_trading: `You have ${tradeCount} trades logged with a ${wr}% win rate, but not enough data in any single pattern category yet. Keep logging trades — especially emotional fields like mental state, focus, and fear level.`,
      root_cause: null,
      top_priority: null,
      connections: [],
      bright_spot: `${insufficient.length} pattern detectors are building data — insights will unlock as you log more trades.`,
      weekly_focus: "Focus on logging every trade with complete fields to unlock deeper pattern analysis.",
      firing_count: 0,
      total_cost: 0,
    };
  }

  const sortedFiring = [...firing].sort((a, b) => b.estimated_cost - a.estimated_cost);
  const topDetector  = sortedFiring[0];
  const totalCost    = firing.reduce((s, d) => s + d.estimated_cost, 0);

  const firingIds = new Set(firing.map(d => d.id));
  const matches   = PATTERN_COMBINATIONS.filter(combo =>
    combo.patterns.every(p => firingIds.has(p))
  );

  const rootCauseSentence = matches.length > 0
    ? `Multiple patterns point to a shared root: ${matches[0].rootCause}. Fixing the root cause will likely resolve several patterns at once.`
    : firing.length > 1
    ? `The ${firing.length} active patterns appear to be independent — each requires its own targeted fix.`
    : null;

  const connections = matches.slice(0, 2).map(m => m.connection);

  let stateOfTrading;
  if (firing.length === 0) {
    stateOfTrading = `With a ${wr}% win rate across ${tradeCount} trades, your mechanical edge is solid. No cost-bearing behavioral patterns are currently active.`;
  } else if (totalCost > 500) {
    stateOfTrading = `Your ${wr}% win rate across ${tradeCount} trades shows a real edge, but behavioral patterns are costing an estimated ${fmt$(totalCost)}/month. The gap between your potential and actual results is measurable — the patterns below are the bridge.`;
  } else if (totalCost > 100) {
    stateOfTrading = `You're trading reasonably well — ${wr}% win rate across ${tradeCount} trades — with ${firing.length} behavioral pattern${firing.length > 1 ? "s" : ""} creating a combined ${fmt$(totalCost)}/month drag. These are fixable with focused attention.`;
  } else {
    stateOfTrading = `Solid foundation: ${wr}% win rate across ${tradeCount} trades with modest behavioral costs (${fmt$(totalCost)}/mo combined). The patterns below are early signals worth addressing before they compound.`;
  }

  let brightSpot;
  const positiveInfo = informational.find(d =>
    ["conviction_mismatch", "setup_edge", "market_regime", "news_event"].includes(d.id)
  );
  if (positiveInfo) {
    brightSpot = `${positiveInfo.title}: ${positiveInfo.summary}`;
  } else if (firing.length < 3) {
    brightSpot = `Only ${firing.length} pattern${firing.length === 1 ? "" : "s"} actively costing money — most behavioral areas are clean.`;
  } else if (wr >= 55) {
    brightSpot = `${wr}% win rate is above average despite the active patterns — fixing these leaks will push your edge significantly higher.`;
  } else {
    brightSpot = `You have ${insufficient.length} patterns still building data — logging emotional fields consistently will unlock deeper insights over the coming weeks.`;
  }

  return {
    state_of_trading:  stateOfTrading,
    root_cause:        rootCauseSentence,
    top_priority: topDetector ? {
      pattern: topDetector.title,
      why:     `At ${fmt$(-topDetector.estimated_cost)}/month, this is your single biggest measurable leak — ${topDetector.confidence} confidence across ${topDetector.n} trades.`,
      rule:    buildRule(topDetector),
    } : null,
    connections,
    bright_spot:   brightSpot,
    weekly_focus:  topDetector ? buildWeeklyFocus(topDetector) : "Focus on logging every trade with complete fields to unlock deeper pattern analysis.",
    firing_count:  firing.length,
    total_cost:    totalCost,
  };
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function InsightSection({ icon, title, children, accentColor }) {
  return (
    <div style={{
      padding: "14px 16px",
      background: (accentColor || C.blue) + "0a",
      border: `1px solid ${(accentColor || C.blue)}25`,
      borderRadius: 10,
      marginBottom: 10,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: accentColor || C.blue, marginBottom: 6 }}>
        {icon} {title}
      </div>
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AIInsights — main export
// ─────────────────────────────────────────────────────────────────────────────
export function AIInsights({ trades = [], analytics, profile, userId, currentAccountId }) {
  // ── Cache: load on mount, keyed by userId + accountId ──────────────────────
  const [insights, setInsights]         = useState(null);
  const [generatedAt, setGeneratedAt]   = useState(null);
  const [cacheLoaded, setCacheLoaded]   = useState(false);

  useEffect(() => {
    const cached = loadCache(userId, currentAccountId);
    if (cached?.insights) {
      setInsights(cached.insights);
      setGeneratedAt(cached.generatedAt);
    }
    setCacheLoaded(true);
  }, [userId, currentAccountId]);

  // Run all 4 detector suites server-side via the run-detectors Edge Function.
  const { data: allSuites, loading: detectorsLoading, error: detectorsError } =
    useDetectors({ suite: "all", trades, profile, userId });

  const allDetectors = useMemo(() => {
    if (!allSuites) return [];
    return [
      ...(allSuites.behavior?.detectors    || []),
      ...(allSuites.risk?.detectors        || []),
      ...(allSuites.context?.detectors     || []),
      ...(allSuites.psychology?.detectors  || []),
    ];
  }, [allSuites]);

  const totalCost = allDetectors
    .filter(d => d.confidence !== "insufficient")
    .reduce((s, d) => s + (d.estimated_cost || 0), 0);

  const firingCount = allDetectors.filter(d =>
    d.confidence !== "insufficient" && d.estimated_cost > 0
  ).length;

  // ── Always-current ref so generate() never closes over stale data ──────────
  const latestRef = useRef({});
  latestRef.current = { allDetectors, trades, analytics, userId, currentAccountId };

  const generate = useCallback(() => {
    const { allDetectors: dets, trades: tr, analytics: an, userId: uid, currentAccountId: acct } = latestRef.current;
    const result = {
      ...synthesize({
        allDetectors: dets,
        tradeCount:   tr.length,
        winRate:      an?.winRate ?? 0,
      }),
      _ts: Date.now(), // unique key forces React to always treat this as a new object
    };
    setInsights(result);
    setGeneratedAt(new Date().toISOString());
    saveCache(uid, acct, result);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable — reads latest values via ref at call time

  // ── Auto-generate on first load if detectors are ready and no cache exists ──
  const didAutoGenerate = useRef(false);
  useEffect(() => {
    if (
      cacheLoaded &&
      !insights &&
      !detectorsLoading &&
      allDetectors.length > 0 &&
      trades.length >= 15 &&
      !didAutoGenerate.current
    ) {
      didAutoGenerate.current = true;
      generate();
    }
  }, [cacheLoaded, detectorsLoading, allDetectors.length, insights, generate, trades.length]);

  // ── Not enough trades ────────────────────────────────────────────────────────
  if (trades.length < 15) {
    return (
      <Card style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🧠</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>
          Log 15+ trades to unlock AI coaching
        </div>
        <div style={{ fontSize: 13, color: C.muted }}>{trades.length} of 15 trades logged</div>
      </Card>
    );
  }

  // ── Detectors still loading and no cache to show yet ────────────────────────
  if (detectorsLoading && !insights) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: "#8892a4", fontSize: 14 }}>
        <span style={{ marginRight: 10 }}>⚙️</span> Analyzing all pattern suites…
      </div>
    );
  }

  if (detectorsError && detectorsError !== "subscription_required" && !insights) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#ff6b6b", fontSize: 13 }}>
        Error loading detectors: {detectorsError}
      </div>
    );
  }

  const ageLabel = formatAge(generatedAt);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <Card style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <SectionTitle>AI Coaching</SectionTitle>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              Synthesizes all {allDetectors.length} detector outputs into a coaching narrative.
              {firingCount > 0 && ` ${firingCount} patterns firing, ${fmt$(totalCost)}/mo combined cost.`}
            </div>
            {/* Cache age label */}
            {ageLabel && (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, opacity: 0.7 }}>
                Generated {ageLabel}
              </div>
            )}
          </div>
          <button
            onClick={generate}
            disabled={detectorsLoading || allDetectors.length === 0}
            style={{
              padding: "10px 20px",
              background: C.purple,
              border: "none", borderRadius: 8,
              color: "#fff", fontWeight: 700, fontSize: 13,
              cursor: (detectorsLoading || allDetectors.length === 0) ? "not-allowed" : "pointer",
              opacity: (detectorsLoading || allDetectors.length === 0) ? 0.6 : 1,
            }}
          >
            {detectorsLoading ? "⚙️ Loading…" : insights ? "↺ Regenerate" : "Generate Insights"}
          </button>
        </div>

        {/* Disclaimer */}
        <div style={{
          marginTop: 12, padding: "8px 12px",
          background: "#1a1d2e",
          border: `1px solid ${C.border}`,
          borderRadius: 6, fontSize: 11, color: C.muted, lineHeight: 1.5,
        }}>
          <strong style={{ color: C.yellow }}>⚠ Not financial advice.</strong>
          {" "}This analysis is based on your logged trade data for educational and self-improvement purposes only.
          Trading futures involves substantial risk of loss. Past patterns do not guarantee future results.
        </div>
      </Card>

      {/* Insights */}
      {insights && (
        <>
          <InsightSection icon="📊" title="State of Your Trading" accentColor={C.blue}>
            {insights.state_of_trading}
          </InsightSection>

          {insights.root_cause && (
            <InsightSection icon="🔍" title="Root Cause" accentColor={C.purple}>
              {insights.root_cause}
            </InsightSection>
          )}

          {insights.top_priority && (
            <div style={{
              padding: "16px 18px",
              background: C.red + "0d",
              border: `1px solid ${C.red}30`,
              borderLeft: `4px solid ${C.red}`,
              borderRadius: 10,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.red, marginBottom: 8 }}>
                🎯 TOP PRIORITY THIS WEEK
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                {insights.top_priority.pattern}
              </div>
              <div style={{ fontSize: 13, color: C.sub, marginBottom: 12, lineHeight: 1.6 }}>
                {insights.top_priority.why}
              </div>
              <div style={{
                padding: "10px 14px", background: "#0b0d19",
                borderRadius: 8, border: `1px solid ${C.border}`,
              }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                  Rule to add this week
                </div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                  "{insights.top_priority.rule}"
                </div>
              </div>
            </div>
          )}

          {insights.connections?.length > 0 && (
            <InsightSection icon="🔗" title="Pattern Connections" accentColor={C.yellow}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {insights.connections.map((c, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{c}</li>
                ))}
              </ul>
            </InsightSection>
          )}

          {insights.bright_spot && (
            <InsightSection icon="✨" title="What's Working" accentColor={C.green}>
              {insights.bright_spot}
            </InsightSection>
          )}

          {insights.weekly_focus && (
            <div style={{
              padding: "14px 18px",
              background: C.green + "0d",
              border: `1px solid ${C.green}30`,
              borderRadius: 10,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.green, marginBottom: 6 }}>
                📅 WEEKLY FOCUS
              </div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}>
                {insights.weekly_focus}
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty state — only shown if cache miss AND detectors not ready yet */}
      {!insights && !detectorsLoading && (
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: C.muted, maxWidth: 400, margin: "0 auto", lineHeight: 1.7 }}>
            Click <strong>Generate Insights</strong> above to synthesize all{" "}
            {allDetectors.length} detector outputs into a prioritized coaching narrative —
            what to focus on, how patterns connect, and one concrete rule to add this week.
          </div>
          {firingCount > 0 && (
            <div style={{ marginTop: 16, fontSize: 13, color: C.red, fontWeight: 600 }}>
              {firingCount} patterns currently firing — {fmt$(totalCost)}/mo combined cost
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
